import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react'; // Importar todos los iconos de Lucide React
import { products, categories } from '../data/mockData'; // Importar datos para el slider
import { useSettings } from '../context/SettingsContext'; // Importar useSettings

const InfoSlider = () => {
  const { settings } = useSettings();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Datos dinámicos para el slider
  const totalProducts = products.length;
  const totalCategories = categories.length;
  const totalUsers = 4; // Simulado, debería venir de un mock de usuarios o API
  const mostVisitedProduct = products.reduce((prev, current) => (prev.visits || 0) > (current.visits || 0) ? prev : current, { visits: 0 });
  const mostVisitedCategory = categories.reduce((prev, current) => (prev.visits || 0) > (current.visits || 0) ? prev : current, { visits: 0 });

  // Función para obtener el valor dinámico del slider
  const getDynamicSliderValue = (slideConfig) => {
    let value = slideConfig.value;
    if (slideConfig.title === 'Productos Activos') {
      value = totalProducts;
    } else if (slideConfig.title === 'Categorías Listadas') {
      value = totalCategories;
    } else if (slideConfig.title === 'Usuarios Registrados') {
      value = totalUsers;
    } else if (slideConfig.title === 'Producto Estrella') {
      value = mostVisitedProduct.name || 'N/A';
      if (mostVisitedProduct.visits) {
        value += ` (${mostVisitedProduct.visits} vistas)`;
      }
    } else if (slideConfig.title === 'Categoría Top') {
      value = mostVisitedCategory.name || 'N/A';
      if (mostVisitedCategory.visits) {
        value += ` (${mostVisitedCategory.visits} vistas)`;
      }
    }
    return value;
  };

  // Mapear la configuración del slider a los datos reales
  const dynamicSlides = settings.sliderInfo.map(slideConfig => {
    const IconComponent = LucideIcons[slideConfig.icon] || LucideIcons.HelpCircle; // Fallback icon

    return {
      ...slideConfig,
      value: getDynamicSliderValue(slideConfig), // Usar la función para obtener el valor dinámico
      IconComponent: IconComponent,
    };
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % dynamicSlides.length);
    }, 5000); // Cambia cada 5 segundos
    return () => clearInterval(interval);
  }, [dynamicSlides.length]);

  const variants = {
    enter: { opacity: 0, x: 100 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 },
  };

  const SlideContent = ({ slide }) => (
    <motion.div
      className="flex items-center gap-4 p-4 bg-white/70 backdrop-blur-md rounded-2xl shadow-md border border-white/20"
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {slide.imageUrl ? (
        <img src={slide.imageUrl} alt={slide.title} className="w-12 h-12 rounded-xl object-cover" />
      ) : (
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${slide.color}`}>
          {slide.IconComponent && <slide.IconComponent className="w-6 h-6 text-white" />}
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-gray-600">{slide.title}</p>
        <h4 className="text-lg font-bold text-gray-900">{slide.value}</h4>
      </div>
    </motion.div>
  );

  return (
    <div className="relative w-full max-w-md mx-auto lg:mx-0">
      <AnimatePresence mode="wait">
        {dynamicSlides.length > 0 && (
          <SlideContent key={currentIndex} slide={dynamicSlides[currentIndex]} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InfoSlider;